import { useCallback, useMemo } from "react";

type SpaceData = {
  id: string;
  name: string;
  parentSpace: string | null;
  inheritEntities: boolean;
};

type UseSpaceSelectionProps = {
  spaces: SpaceData[];
  selectedSpaceIds: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  defaultSelection?: string;
};

/**
 * Custom hook for managing hierarchical space selection with inheritance logic.
 *
 * This hook implements level-by-level inheritance where:
 * - Spaces with `inheritEntities: true` automatically inherit selection from their direct parent
 * - Inheritance cascades: parent → child → grandchild (but only if each level is checked)
 * - The chain breaks if an intermediate parent is NOT checked
 * - Non-inheriting children must be explicitly selected even when parent is selected
 * - When all inheriting children are manually selected, the parent is auto-selected
 *
 * @example
 * ```
 * root (selected)
 *   ├─ space-1 (inheritEntities: true) → ✅ Checked & Disabled (inherited from root)
 *   │   ├─ space-1-a (inheritEntities: true) → ✅ Checked & Disabled (inherited from space-1)
 *   │   └─ space-1-b (inheritEntities: false) → ❌ NOT checked (must be manually selected)
 *   └─ space-2 (inheritEntities: false) → ❌ NOT checked (must be manually selected)
 * ```
 *
 * @param spaces - Array of all available spaces in the hierarchy
 * @param selectedSpaceIds - Set of space IDs that are explicitly selected by the user
 * @param onSelectionChange - Callback fired when selection changes, receives updated selectedSpaceIds
 * @returns Object containing checkedSpaceIds (visible state), disabledSpaceIds, and handleSelectionChange
 */
export const useSpaceSelection = ({
  spaces,
  selectedSpaceIds,
  onSelectionChange,
  defaultSelection,
}: UseSpaceSelectionProps) => {
  /**
   * Recursively collects all descendant space IDs for a given space.
   * Used for cascading operations like removing all children when parent is unchecked.
   *
   * @param spaceId - The parent space ID to find descendants for
   * @returns Array of all descendant space IDs (children, grandchildren, etc.)
   */
  const getDescendants = useCallback(
    (spaceId: string): string[] => {
      const descendants: string[] = [];
      const findDescendants = (id: string) => {
        const children = spaces.filter((space) => space.parentSpace === id);
        children.forEach((child) => {
          descendants.push(child.id);
          findDescendants(child.id);
        });
      };
      findDescendants(spaceId);
      return descendants;
    },
    [spaces]
  );

  /**
   * Helper to check if a space would be checked via inheritance from its parent.
   * This is used during normalization to avoid adding spaces that would already
   * be checked through inheritance.
   */
  const wouldBeChecked = useCallback(
    (spaceId: string, normalized: Set<string>): boolean => {
      if (normalized.has(spaceId)) return true;

      const space = spaces.find((s) => s.id === spaceId);
      if (!space) return false;

      // If this space inherits and its parent is checked, it would be checked via inheritance
      if (space.inheritEntities && space.parentSpace) {
        // eslint-disable-next-line react-hooks/immutability
        return wouldBeChecked(space.parentSpace, normalized);
      }

      return false;
    },
    [spaces]
  );

  /**
   * Filters children to exclude defaultSelection.
   * This ensures defaultSelection doesn't trigger parent auto-selection.
   *
   * @param children - Array of child spaces
   * @returns Object with filtered inheriting and non-inheriting children
   */
  const filterChildrenForAutoSelection = useCallback(
    (children: SpaceData[]) => {
      const inheritingChildren = children.filter(
        (c) => c.inheritEntities && c.id !== defaultSelection
      );
      const nonInheritingChildren = children.filter(
        (c) => !c.inheritEntities && c.id !== defaultSelection
      );

      return { inheritingChildren, nonInheritingChildren };
    },
    [defaultSelection]
  );

  /**
   * Determines if a parent should be auto-selected based on its children's state.
   *
   * Auto-selection rules:
   * 1. All inheriting children are selected → auto-select parent
   * 2. All children (both inheriting AND non-inheriting) are selected → auto-select parent
   * 3. Only non-inheriting children selected → do NOT auto-select parent
   *
   * Rationale: Non-inheriting children don't have a semantic relationship with their parent,
   * so selecting them alone shouldn't automatically select the parent.
   *
   * @param inheritingChildren - Filtered inheriting children (excluding defaultSelection)
   * @param nonInheritingChildren - Filtered non-inheriting children (excluding defaultSelection)
   * @param normalized - Current normalized set of space IDs
   * @returns true if parent should be auto-selected
   */
  const shouldAutoSelectParent = useCallback(
    (
      inheritingChildren: SpaceData[],
      nonInheritingChildren: SpaceData[],
      normalized: Set<string>
    ): boolean => {
      // Check if all inheriting children are selected
      const allInheritingChildrenSelected =
        inheritingChildren.length > 0 &&
        inheritingChildren.every((child) => normalized.has(child.id));

      // Check if all non-inheriting children are selected
      const allNonInheritingChildrenSelected =
        nonInheritingChildren.length > 0 &&
        nonInheritingChildren.every((child) => normalized.has(child.id));

      // Case 1: All inheriting children selected → auto-select
      if (allInheritingChildrenSelected) {
        return true;
      }

      // Case 2: All children (both types) selected → auto-select
      if (allNonInheritingChildrenSelected && allInheritingChildrenSelected) {
        return true;
      }

      // Case 3: Only non-inheriting children exist/selected → do NOT auto-select
      return false;
    },
    []
  );

  /**
   * Removes inheriting children from the normalized set when their parent is auto-selected.
   * This reduces redundancy - inheriting children will be checked via inheritance.
   *
   * @param normalized - Set to remove inheriting children from
   * @param inheritingChildren - Children that inherit from the parent
   * @returns Object with new Set and a boolean indicating if any changes were made
   */
  const removeInheritingChildren = useCallback(
    (
      normalized: Set<string>,
      inheritingChildren: SpaceData[]
    ): { newSet: Set<string>; changed: boolean } => {
      const newSet = new Set(normalized);
      let changed = false;

      inheritingChildren.forEach((child) => {
        // Don't remove defaultSelection even if it's inheriting
        if (newSet.has(child.id) && child.id !== defaultSelection) {
          newSet.delete(child.id);
          changed = true;
        }
      });

      return { newSet, changed };
    },
    [defaultSelection]
  );

  /**
   * Normalizes a set of space IDs by auto-selecting parents when all their children are selected.
   * This ensures that if all children are selected, the parent is also auto-selected.
   * Runs iteratively to handle cascading parent selection up the hierarchy.
   *
   * Key behaviors:
   * - Parents are auto-selected when ALL their (non-defaultSelection) children are selected
   * - Parents are NOT auto-selected if only non-inheriting children exist
   * - Parents that would already be checked via inheritance are not added
   * - Spaces in excludeSpaces are never auto-selected (respects explicit user unchecks)
   * - defaultSelection is treated as invisible for parent auto-selection purposes
   *
   * @param spaceIds - Set of space IDs to normalize
   * @param excludeSpaces - Set of space IDs to exclude from normalization (e.g., explicitly unchecked)
   * @returns Normalized set with auto-selected parents
   */
  const normalizeSpaceIds = useCallback(
    (spaceIds: Set<string>, excludeSpaces: Set<string> = new Set()): Set<string> => {
      const normalized = new Set(spaceIds);

      // Run iteratively until no more changes (handles cascading up the hierarchy)
      let changed = true;
      let iterations = 0;
      const MAX_ITERATIONS = 50; // Safety limit to prevent infinite loops

      while (changed && iterations < MAX_ITERATIONS) {
        changed = false;
        iterations++;

        spaces.forEach((space) => {
          // Skip if already selected
          if (normalized.has(space.id)) return;

          // Skip if explicitly excluded (user unchecked it)
          if (excludeSpaces.has(space.id)) return;

          // Get children of this space
          const children = spaces.filter((s) => s.parentSpace === space.id);
          if (children.length === 0) return;

          // Filter children (excluding defaultSelection)
          const { inheritingChildren, nonInheritingChildren } =
            filterChildrenForAutoSelection(children);

          // Determine if parent should be auto-selected
          if (shouldAutoSelectParent(inheritingChildren, nonInheritingChildren, normalized)) {
            // Check if this parent would already be checked via inheritance
            const parentWouldBeChecked =
              space.inheritEntities &&
              space.parentSpace &&
              wouldBeChecked(space.parentSpace, normalized);

            // Only add parent if it wouldn't already be checked via inheritance
            if (!parentWouldBeChecked) {
              normalized.add(space.id);
              changed = true;
            }

            // Remove inheriting children (they'll inherit from this parent)
            const { newSet, changed: childrenChanged } = removeInheritingChildren(
              normalized,
              inheritingChildren
            );
            if (childrenChanged) {
              // Replace normalized with the new set
              normalized.clear();
              newSet.forEach((id) => normalized.add(id));
              changed = true;
            }
          }
        });
      }

      if (iterations >= MAX_ITERATIONS) {
        console.warn("SpaceSelector normalization exceeded max iterations", {
          spacesCount: spaces.length,
          initialCount: spaceIds.size,
          finalCount: normalized.size,
        });
      }

      return normalized;
    },
    [
      spaces,
      wouldBeChecked,
      filterChildrenForAutoSelection,
      shouldAutoSelectParent,
      removeInheritingChildren,
    ]
  );

  /**
   * Calculates the complete set of checked spaces (visible UI state).
   *
   * This includes:
   * 1. All explicitly selected spaces (from selectedSpaceIds)
   * 2. All spaces that inherit from their parent (cascading down the hierarchy)
   *
   * Uses a breadth-first traversal approach to propagate inheritance efficiently.
   * Starting from selected spaces, we traverse down the hierarchy, checking each
   * level's children to see if they should inherit.
   *
   * Performance: O(n) where n = number of spaces (each space is visited at most once)
   * This is significantly better than the previous O(n * d) iterative approach.
   *
   * @returns Set of all space IDs that should appear checked in the UI
   */
  const checkedSpaceIds = useMemo(() => {
    const checked = new Set<string>();

    // Add all directly selected spaces
    selectedSpaceIds.forEach((id) => checked.add(id));

    // Build a map of parent -> children for efficient lookup
    const childrenMap = new Map<string, SpaceData[]>();
    spaces.forEach((space) => {
      if (space.parentSpace) {
        if (!childrenMap.has(space.parentSpace)) {
          childrenMap.set(space.parentSpace, []);
        }
        childrenMap.get(space.parentSpace)!.push(space);
      }
    });

    // BFS: Process spaces level-by-level starting from selected spaces
    const queue = Array.from(selectedSpaceIds);

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const children = childrenMap.get(currentId) || [];

      // Check each child to see if it should inherit
      children.forEach((child) => {
        // Only inherit if child has inheritEntities: true and isn't already checked
        if (child.inheritEntities && !checked.has(child.id)) {
          checked.add(child.id);
          // Add to queue to check this child's descendants
          queue.push(child.id);
        }
      });
    }

    return checked;
  }, [selectedSpaceIds, spaces]);

  /**
   * Calculates which spaces should be disabled (checked but not toggleable).
   *
   * A space is disabled when:
   * - It has `inheritEntities: true` AND it's checked via inheritance (not in selectedSpaceIds)
   *
   * These spaces appear checked but users cannot uncheck them directly - they must uncheck
   * the parent they're inheriting from.
   *
   * @returns Array of space IDs that should be disabled in the UI
   */
  const disabledSpaceIds = useMemo(() => {
    const disabled: string[] = [];

    spaces.forEach((space) => {
      // Space inherits from parent and is auto-checked (not manually selected)
      if (
        space.inheritEntities &&
        checkedSpaceIds.has(space.id) &&
        !selectedSpaceIds.has(space.id)
      ) {
        disabled.push(space.id);
      }
    });

    return disabled;
  }, [spaces, checkedSpaceIds, selectedSpaceIds]);

  /**
   * Step 1: Identifies all spaces that need to be removed from selection.
   * When a parent is unchecked, ALL its descendants must be removed (both inheriting and non-inheriting).
   *
   * @param uncheckedSpaces - Set of spaces that were previously selected but are no longer in newCheckedIds
   * @returns Set of all space IDs to remove (unchecked spaces + all their descendants)
   */
  const calculateSpacesToRemove = useCallback(
    (uncheckedSpaces: Set<string>): Set<string> => {
      const spacesToRemove = new Set<string>();
      uncheckedSpaces.forEach((uncheckedId) => {
        spacesToRemove.add(uncheckedId);
        const descendants = getDescendants(uncheckedId);
        descendants.forEach((descendantId) => {
          spacesToRemove.add(descendantId);
        });
      });
      return spacesToRemove;
    },
    [getDescendants]
  );

  /**
   * Step 2: Processes checked spaces to determine which should be in the final selectedSpaceIds.
   *
   * Key logic:
   * - Root spaces (no parent) are always added
   * - Non-inheriting children are always added (even when parent is selected)
   * - Inheriting children are excluded if their direct parent is checked (they'll inherit instead)
   *
   * @param newCheckedIds - All space IDs that appear checked in the UI
   * @param spacesToRemove - Spaces being removed due to parent unchecking
   * @returns Set of space IDs that should be explicitly in selectedSpaceIds
   */
  const processCheckedSpaces = useCallback(
    (newCheckedIds: Set<string>, spacesToRemove: Set<string>): Set<string> => {
      const finalSelectedIds = new Set<string>();

      newCheckedIds.forEach((id) => {
        // Skip if this space should be removed (its parent was unchecked)
        if (spacesToRemove.has(id)) return;

        const space = spaces.find((s) => s.id === id);
        if (!space) return;

        // Check if direct parent is checked
        const directParentChecked = space.parentSpace && newCheckedIds.has(space.parentSpace);

        // Add to finalSelectedIds if:
        // 1. It's a root selection (no parent), OR
        // 2. It's a non-inheriting child (should be in selectedSpaceIds even when parent is selected), OR
        // 3. Its direct parent is NOT checked (not auto-inherited)
        if (!space.parentSpace || !space.inheritEntities || !directParentChecked) {
          finalSelectedIds.add(id);
        }
      });

      return finalSelectedIds;
    },
    [spaces]
  );

  /**
   * Step 3: Adds non-inheriting descendants when their parent is newly selected.
   *
   * When a parent is selected, non-inheriting children don't automatically get checked
   * (unlike inheriting children). We must explicitly add them to selectedSpaceIds
   * to maintain their checked state.
   *
   * @param finalSelectedIds - Set to add non-inheriting descendants to
   * @returns New Set with non-inheriting descendants added
   */
  const addNonInheritingDescendants = useCallback(
    (finalSelectedIds: Set<string>): Set<string> => {
      const newSet = new Set(finalSelectedIds);

      finalSelectedIds.forEach((id) => {
        const wasAlreadySelected = selectedSpaceIds.has(id);

        // If this is a newly selected space, add all non-inheriting descendants
        if (!wasAlreadySelected) {
          const descendants = getDescendants(id);
          descendants.forEach((descendantId) => {
            const descendant = spaces.find((s) => s.id === descendantId);
            if (descendant && !descendant.inheritEntities) {
              newSet.add(descendantId);
            }
          });
        }
      });

      return newSet;
    },
    [selectedSpaceIds, getDescendants, spaces]
  );

  /**
   * Main handler for selection changes from the UI.
   *
   * This orchestrates the complex selection logic through four steps:
   * 1. Calculate spaces to remove (unchecked parents and all their descendants)
   * 2. Process checked spaces to determine which should be explicitly selected
   * 3. Add non-inheriting descendants of newly selected parents
   * 4. Normalize the selection to handle cascading parent auto-selection
   *
   * The goal is to maintain a minimal set in selectedSpaceIds while ensuring
   * the correct spaces appear checked via inheritance logic.
   *
   * @param newCheckedIds - Set of all space IDs that appear checked in the UI (user's desired state)
   */
  const handleSelectionChange = useCallback(
    (newCheckedIds: Set<string>) => {
      if (!onSelectionChange) return;

      // Step 1: Find spaces that were unchecked and need to be removed with descendants
      // Compare against the ORIGINAL selectedSpaceIds (not normalized) to detect user changes
      const uncheckedSpaces = new Set<string>();
      selectedSpaceIds.forEach((id) => {
        if (!newCheckedIds.has(id)) {
          uncheckedSpaces.add(id);
        }
      });
      const spacesToRemove = calculateSpacesToRemove(uncheckedSpaces);

      // Step 2: Process checked spaces to build the base selection
      const baseSelectedIds = processCheckedSpaces(newCheckedIds, spacesToRemove);

      // Step 3: Add non-inheriting descendants for newly selected parents
      const finalSelectedIds = addNonInheritingDescendants(baseSelectedIds);

      // Step 4: Normalize the final selection to handle cascading parent auto-selection
      // This replaces the old Step 5 (autoSelectParentsWhenAllChildrenChecked) with a more
      // comprehensive approach that handles multi-level cascading
      // Pass uncheckedSpaces to prevent re-adding spaces the user explicitly unchecked
      const normalizedFinalSelectedIds = normalizeSpaceIds(finalSelectedIds, uncheckedSpaces);

      onSelectionChange(normalizedFinalSelectedIds);
    },
    [
      selectedSpaceIds,
      onSelectionChange,
      calculateSpacesToRemove,
      processCheckedSpaces,
      addNonInheritingDescendants,
      normalizeSpaceIds,
    ]
  );

  return {
    checkedSpaceIds,
    disabledSpaceIds,
    handleSelectionChange,
  };
};
